// Header alerts bell (U3) — legacy AlertsComponent: `GET alerts` counts with
// deep links into the filtered list pages / reports.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Box, CircularProgress, IconButton, List, ListItemButton, ListItemText, Popover, Tooltip, Typography } from '@mui/material';
import { Notification } from 'iconsax-reactjs';

import { onLocationChanged } from '../../../session/location';
import { t } from '../../../i18n';
import { AlertCounts, fetchAlerts } from '../api';
import { UI } from '../../../brand/ui';

type AlertLink = { key: keyof AlertCounts; to: string; labelKey: string };

const ALERT_LINKS: AlertLink[] = [
  { key: 'notifications', to: 'notifications', labelKey: 'shell.alert.notifications' },
  { key: 'payment_reviews', to: 'payments?review=1', labelKey: 'shell.alert.paymentReviews' },
  { key: 'payments', to: 'payments?due=1', labelKey: 'shell.alert.duePayments' },
  { key: 'expenses', to: 'expenses?require_approval=1', labelKey: 'shell.alert.expensesApproval' },
  { key: 'recurring_soon', to: 'recurring-sales?in_days=7', labelKey: 'shell.alert.recurringSoon' },
  { key: 'quantity_alert', to: 'reports/quantity-alerts', labelKey: 'shell.alert.quantity' },
  { key: 'expiry_alert', to: 'reports/expiring-alerts', labelKey: 'shell.alert.expiry' },
  { key: 'customers', to: 'customers?due_limit=80', labelKey: 'shell.alert.customersDue' },
  { key: 'suppliers', to: 'suppliers?due_limit=80', labelKey: 'shell.alert.suppliersDue' }
];

const POLL_MS = 120_000;

/** Fire to open the alerts popover from anywhere (keyboard shortcut ctrl+alt+a). */
export const OPEN_ALERTS_EVENT = 'vendia:open-alerts';
/** Fire to make the bell refetch (e.g. after reading notifications). */
export const REFRESH_ALERTS_EVENT = 'vendia:refresh-alerts';

export function AlertsBell() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [alerts, setAlerts] = useState<AlertCounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchAlerts()
      .then(setAlerts)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    const offLocation = onLocationChanged(load);
    window.addEventListener(REFRESH_ALERTS_EVENT, load);
    return () => {
      window.clearInterval(timer);
      offLocation();
      window.removeEventListener(REFRESH_ALERTS_EVENT, load);
    };
  }, [load]);

  useEffect(() => {
    const open = () => {
      load();
      setAnchor(buttonEl);
    };
    window.addEventListener(OPEN_ALERTS_EVENT, open);
    return () => window.removeEventListener(OPEN_ALERTS_EVENT, open);
  }, [buttonEl, load]);

  const total = alerts ? ALERT_LINKS.reduce((sum, link) => sum + (Number(alerts[link.key]) || 0), 0) : 0;
  const active = alerts ? ALERT_LINKS.filter((link) => Number(alerts[link.key]) > 0) : [];

  return (
    <>
      <Tooltip title={t('shell.alerts')}>
        <IconButton
          ref={setButtonEl}
          aria-label={t('shell.alerts')}
          onClick={(event) => {
            load();
            setAnchor(event.currentTarget);
          }}
        >
          <Badge color="error" badgeContent={total} max={99}>
            <Notification size={20} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('shell.alerts')}
          </Typography>
          {loading ? <CircularProgress size={14} /> : null}
        </Box>
        {active.length ? (
          <List dense disablePadding data-testid="alerts-list">
            {active.map((link) => (
              <ListItemButton
                key={link.key}
                onClick={() => {
                  setAnchor(null);
                  navigate(`/${link.to}`);
                }}
              >
                <Box
                  sx={{
                    minWidth: 40,
                    mr: 1.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: UI.radiusSm,
                    textAlign: 'center',
                    fontWeight: 700,
                    bgcolor: UI.accentAlpha(0.10),
                    color: 'primary.main'
                  }}
                >
                  {Number(alerts?.[link.key] ?? 0).toLocaleString()}
                </Box>
                <ListItemText primary={t(link.labelKey)} />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
            {t('shell.noAlerts')}
          </Typography>
        )}
      </Popover>
    </>
  );
}
